import { IText, util } from 'fabric';
import type { ITextProps, TPathSide, TPointerEvent } from 'fabric';

interface CurvedITextOptions extends Partial<ITextProps> {
  pathStartOffset?: number;
  pathSide?: TPathSide;
}

/**
 * IText subclass that renders cursor and selection along the text path
 * when editing text-on-path (circle, arch, etc.).
 *
 * Fabric.js positions each character on the path with renderLeft/renderTop/angle
 * in __charBounds, but the default cursor/selection rendering ignores those values
 * and draws as if text were flat. This subclass fixes that.
 */
export class CurvedIText extends IText {
  constructor(text: string, options?: CurvedITextOptions) {
    super(text, options as Partial<ITextProps>);
    if (options?.pathStartOffset != null) {
      this.pathStartOffset = options.pathStartOffset;
    }
  }

  /**
   * Override: find the closest character to the click point using path positions.
   * The default uses flat x/y scanning which doesn't work for text on a curve.
   */
  getSelectionStartFromPointer(e: TPointerEvent): number {
    if (!this.path) {
      return super.getSelectionStartFromPointer(e);
    }

    // Transform the click point from scene coords into the object's local coords
    const mouseOffset = this.canvas!.getScenePoint(e)
      .transform(util.invertTransform(this.calcTransformMatrix()));

    let bestIndex = 0;
    let bestDist = Infinity;
    let flatIndex = 0;

    for (let lineIdx = 0; lineIdx < this._textLines.length; lineIdx++) {
      const lineBounds = this.__charBounds?.[lineIdx];
      if (!lineBounds) {
        flatIndex += this._textLines[lineIdx].length + this.missingNewlineOffset(lineIdx);
        continue;
      }

      for (let charIdx = 0; charIdx < lineBounds.length; charIdx++) {
        const box = lineBounds[charIdx];
        if (box.renderLeft == null || box.renderTop == null) {
          flatIndex++;
          continue;
        }

        // Distance from click to character center
        const dx = mouseOffset.x - box.renderLeft;
        const dy = mouseOffset.y - box.renderTop;
        const dist = dx * dx + dy * dy;

        if (dist < bestDist) {
          bestDist = dist;
          // Determine if click is closer to the left or right edge of the character.
          // Project the click offset onto the character's tangent direction to decide
          // whether the cursor goes before or after this character.
          const angle = box.angle ?? 0;
          const along = dx * Math.cos(angle) + dy * Math.sin(angle);
          bestIndex = along > 0 ? flatIndex + 1 : flatIndex;
        }
        flatIndex++;
      }
      // Account for newline offset between lines
      if (lineIdx < this._textLines.length - 1) {
        flatIndex += this.missingNewlineOffset(lineIdx);
      }
    }

    return Math.min(bestIndex, this._text.length);
  }

  /**
   * Override clearContextTop to clear a larger area for path text.
   * The default clears width+4 × height+4 around the object center,
   * but path characters render at renderLeft/renderTop positions that
   * can extend far beyond that (especially on circles).
   */
  clearContextTop(restoreManually?: boolean): CanvasRenderingContext2D | undefined {
    if (!this.path) {
      return super.clearContextTop(restoreManually);
    }
    if (!this.canvas) return;
    const ctx = this.canvas.contextTop;
    if (!ctx) return;

    const v = this.canvas.viewportTransform;
    ctx.save();
    ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
    this.transform(ctx);

    // For path text, compute the actual bounding extent from all character positions
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const fontSize = this.fontSize || 40;
    const margin = fontSize * 2; // generous margin for character height + rotation
    for (const lineBounds of this.__charBounds ?? []) {
      for (const box of lineBounds ?? []) {
        if (box.renderLeft != null && box.renderTop != null) {
          minX = Math.min(minX, box.renderLeft);
          minY = Math.min(minY, box.renderTop);
          maxX = Math.max(maxX, box.renderLeft);
          maxY = Math.max(maxY, box.renderTop);
        }
      }
    }
    if (minX === Infinity) {
      // Fallback if no path data
      const w = this.width + 4, h = this.height + 4;
      ctx.clearRect(-w / 2, -h / 2, w, h);
    } else {
      ctx.clearRect(
        minX - margin,
        minY - margin,
        (maxX - minX) + margin * 2,
        (maxY - minY) + margin * 2,
      );
    }

    if (!restoreManually) ctx.restore();
    return ctx;
  }

  /**
   * Override: when a path is present, draw cursor and selection per-character
   * using the path-positioned coordinates. Falls back to default for straight text.
   */
  renderCursorOrSelection() {
    if (!this.path || !this.isEditing || !this.canvas) {
      super.renderCursorOrSelection();
      return;
    }

    const ctx = this.clearContextTop(true);
    if (!ctx) return;

    if (this.selectionStart === this.selectionEnd && !this.inCompositionMode) {
      this._renderCursorOnPath(ctx);
    } else {
      this._renderSelectionOnPath(ctx);
    }

    this.canvas.contextTopDirty = true;
    ctx.restore();
  }

  /**
   * Draw the blinking cursor at the correct position and angle on the path.
   */
  private _renderCursorOnPath(ctx: CanvasRenderingContext2D) {
    if (this._currentCursorOpacity <= 0) return;

    const { lineIndex, charIndex } = this.get2DCursorLocation(this.selectionStart);
    const lineBounds = this.__charBounds?.[lineIndex];
    if (!lineBounds) return;

    // Cursor goes *before* the character at selectionStart.
    // If at end of line, place it after the last character.
    const atEnd = charIndex >= lineBounds.length;
    const refIndex = atEnd ? lineBounds.length - 1 : charIndex;
    const charBox = lineBounds[refIndex];
    if (!charBox || charBox.renderLeft == null || charBox.renderTop == null || charBox.angle == null) {
      super.renderCursorOrSelection();
      return;
    }

    const fontSize = this.getValueOfPropertyAt(lineIndex, Math.max(0, refIndex), 'fontSize') as number;
    const multiplier = this.getObjectScaling().x * this.canvas!.getZoom();
    const cursorWidth = this.cursorWidth / multiplier;
    const cursorColor = this.cursorColor ||
      (this.getValueOfPropertyAt(lineIndex, Math.max(0, refIndex), 'fill') as string);

    // renderLeft/renderTop is the character center on the path.
    // Offset along the path tangent to get the left or right edge.
    let renderLeft = charBox.renderLeft;
    let renderTop = charBox.renderTop;
    if (atEnd) {
      renderLeft += Math.cos(charBox.angle) * (charBox.kernedWidth / 2);
      renderTop += Math.sin(charBox.angle) * (charBox.kernedWidth / 2);
    } else {
      renderLeft -= Math.cos(charBox.angle) * (charBox.kernedWidth / 2);
      renderTop -= Math.sin(charBox.angle) * (charBox.kernedWidth / 2);
    }

    ctx.save();
    ctx.translate(renderLeft, renderTop);
    ctx.rotate(charBox.angle);
    ctx.fillStyle = cursorColor;
    ctx.globalAlpha = this._currentCursorOpacity;
    ctx.fillRect(
      -cursorWidth / 2,
      -fontSize * (1 - this._fontSizeFraction),
      cursorWidth,
      fontSize,
    );
    ctx.restore();
  }

  /**
   * Draw selection highlights per-character along the path.
   * Uses fontSize for height (not lineHeight which includes the multiplier).
   */
  private _renderSelectionOnPath(ctx: CanvasRenderingContext2D) {
    const selStart = this.inCompositionMode
      ? this.hiddenTextarea!.selectionStart
      : this.selectionStart;
    const selEnd = this.inCompositionMode
      ? this.hiddenTextarea!.selectionEnd
      : this.selectionEnd;

    const start2D = this.get2DCursorLocation(selStart);
    const end2D = this.get2DCursorLocation(selEnd);

    ctx.fillStyle = this.selectionColor;

    for (let lineIdx = start2D.lineIndex; lineIdx <= end2D.lineIndex; lineIdx++) {
      const lineBounds = this.__charBounds?.[lineIdx];
      if (!lineBounds) continue;

      const firstChar = lineIdx === start2D.lineIndex ? start2D.charIndex : 0;
      const lastChar = lineIdx === end2D.lineIndex ? end2D.charIndex : lineBounds.length;

      for (let charIdx = firstChar; charIdx < lastChar; charIdx++) {
        const charBox = lineBounds[charIdx];
        if (!charBox || charBox.renderLeft == null || charBox.renderTop == null || charBox.angle == null) {
          continue;
        }

        const charFontSize = this.getValueOfPropertyAt(lineIdx, charIdx, 'fontSize') as number;

        ctx.save();
        ctx.translate(charBox.renderLeft, charBox.renderTop);
        ctx.rotate(charBox.angle);

        if (this.inCompositionMode) {
          ctx.fillStyle = this.compositionColor || 'black';
          ctx.fillRect(
            -charBox.kernedWidth / 2,
            0,
            charBox.kernedWidth,
            1,
          );
        } else {
          ctx.fillRect(
            -charBox.kernedWidth / 2,
            -charFontSize * (1 - this._fontSizeFraction),
            charBox.kernedWidth,
            charFontSize,
          );
        }
        ctx.restore();
      }
    }
  }
}
